<template>
  <div v-if="loading" class="flex justify-center py-12" role="status">
    <font-awesome-icon class="h-6 w-6 text-gray-400 animate-spin" icon="spinner"/>
    <span class="sr-only">{{ $t('projects.course_details_loading') }}</span>
  </div>

  <form v-else novalidate class="space-y-6" @submit.prevent="save">
    <div>
      <label for="course-title" class="block text-sm font-medium text-gray-700">{{ $t('projects.course_title_label') }}</label>
      <input id="course-title" v-model="form.title" type="text" maxlength="255"
             :aria-invalid="!!errors.title" :aria-describedby="errors.title ? 'course-title-error' : null"
             :class="inputClass">
      <p v-if="errors.title" id="course-title-error" class="mt-2 text-sm text-red-600">{{ errors.title }}</p>
    </div>

    <div>
      <label for="course-subtitle" class="block text-sm font-medium text-gray-700">{{ $t('projects.course_subtitle_label') }}</label>
      <input id="course-subtitle" v-model="form.subtitle" type="text" maxlength="255"
             :aria-invalid="!!errors.subtitle" :aria-describedby="errors.subtitle ? 'course-subtitle-error' : null"
             :class="inputClass">
      <p v-if="errors.subtitle" id="course-subtitle-error" class="mt-2 text-sm text-red-600">{{ errors.subtitle }}</p>
    </div>

    <div>
      <span id="course-description-label" class="block text-sm font-medium text-gray-700">{{ $t('projects.course_description_label') }}</span>
      <div class="mt-1" role="group" aria-labelledby="course-description-label">
        <tip-tap-editor-component v-model="form.description"></tip-tap-editor-component>
      </div>
      <p v-if="errors.description" class="mt-2 text-sm text-red-600">{{ errors.description }}</p>
    </div>

    <div>
      <label for="course-cover" class="block text-sm font-medium text-gray-700">{{ $t('projects.course_cover_label') }}</label>
      <div class="mt-1 flex flex-col sm:flex-row sm:items-center gap-4">
        <div class="w-full sm:w-64 aspect-w-16 aspect-h-9 rounded-md overflow-hidden bg-gray-100 border border-gray-200">
          <img v-if="coverPreviewUrl" :src="coverPreviewUrl" :alt="$t('projects.course_cover_alt')" class="object-cover">
          <div v-else class="flex items-center justify-center text-sm text-gray-400">{{ $t('projects.course_cover_empty') }}</div>
        </div>
        <div>
          <input id="course-cover" type="file" accept="image/*" @change="onCoverSelected"
                 :aria-describedby="errors.cover ? 'course-cover-error course-cover-help' : 'course-cover-help'"
                 class="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-bpblue">
          <p id="course-cover-help" class="mt-2 text-xs text-gray-500">{{ $t('projects.course_cover_help') }}</p>
          <p v-if="errors.cover" id="course-cover-error" class="mt-2 text-sm text-red-600">{{ errors.cover }}</p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <div>
        <label for="course-slug" class="block text-sm font-medium text-gray-700">{{ $t('projects.course_slug_label') }}</label>
        <input id="course-slug" v-model="form.slug" type="text" maxlength="255" autocomplete="off"
               :aria-invalid="!!errors.slug" :aria-describedby="errors.slug ? 'course-slug-error course-slug-help' : 'course-slug-help'"
               :class="inputClass">
        <p id="course-slug-help" class="mt-2 text-xs text-gray-500">{{ $t('projects.course_slug_help') }}</p>
        <p v-if="errors.slug" id="course-slug-error" class="mt-2 text-sm text-red-600">{{ errors.slug }}</p>
      </div>

      <div>
        <label for="course-status" class="block text-sm font-medium text-gray-700">{{ $t('projects.status_label') }}</label>
        <select id="course-status" v-model="form.status"
                class="mt-1 block w-full px-3 py-2 text-sm text-gray-700 rounded-md bg-white border border-gray-300 focus:outline-none focus:ring-bpblue focus:border-bpblue">
          <option v-for="status in statuses" :key="status.value" :value="status.value">{{ $t(status.label) }}</option>
        </select>
        <p v-if="errors.status" class="mt-2 text-sm text-red-600">{{ errors.status }}</p>
      </div>
    </div>

    <div class="flex justify-end">
      <button type="submit" :disabled="saving || !isDirty"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        <font-awesome-icon v-if="saving" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
        {{ $t('general.save') }}
      </button>
    </div>
  </form>
</template>

<script>
import TipTapEditorComponent from "@/components/editor/TipTapEditorComponent";
import CourseService from "@/services/project/CourseService";
import NotificationService from "@/services/notification/NotificationService";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const MAX_COVER_BYTES = 5 * 1024 * 1024

const toForm = (course) => ({
  title: course.title ?? '',
  subtitle: course.subtitle ?? '',
  description: course.description ?? '',
  slug: course.slug ?? '',
  status: course.status ?? 'draft',
})

export default {
  name: "CourseDetailsForm",
  components: {TipTapEditorComponent},
  props: {
    courseUuid: {
      type: String,
      required: true
    }
  },
  emits: ['saved'],
  data() {
    return {
      loading: true,
      saving: false,
      form: toForm({}),
      savedForm: toForm({}),
      cover: null,
      coverUrl: '',
      localCoverUrl: '',
      errors: {},
      // Estados del curso (LMS-052): `publish` mantiene el valor actual de `projects.status`.
      statuses: [
        {value: 'draft', label: 'projects.draft'},
        {value: 'publish', label: 'projects.published'},
        {value: 'archived', label: 'projects.archived'},
      ],
      inputClass: 'mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-bpblue focus:border-bpblue sm:text-sm',
    }
  },
  computed: {
    isDirty() {
      return !!this.cover || JSON.stringify(this.form) !== JSON.stringify(this.savedForm)
    },
    coverPreviewUrl() {
      return this.localCoverUrl || this.coverUrl
    }
  },
  watch: {
    courseUuid: {
      handler() {
        this.load()
      },
      immediate: true
    }
  },
  beforeUnmount() {
    this.releaseLocalCover()
  },
  methods: {
    load() {
      this.loading = true

      CourseService.show(this.courseUuid)
          .then((response) => this.applyCourse(response.data?.data ?? response.data))
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.loading = false
          })
    },
    applyCourse(course) {
      this.form = toForm(course)
      this.savedForm = toForm(course)
      this.coverUrl = course.cover_url ?? ''
      this.cover = null
      this.releaseLocalCover()
    },
    onCoverSelected(event) {
      const file = event.target.files?.[0] ?? null
      this.errors = {...this.errors, cover: ''}

      if (file && (!file.type.startsWith('image/') || file.size > MAX_COVER_BYTES)) {
        this.errors.cover = this.$t('projects.course_cover_invalid')
        event.target.value = ''
        return
      }

      this.releaseLocalCover()
      this.cover = file
      this.localCoverUrl = file ? URL.createObjectURL(file) : ''
    },
    releaseLocalCover() {
      if (this.localCoverUrl) {
        URL.revokeObjectURL(this.localCoverUrl)
        this.localCoverUrl = ''
      }
    },
    validate() {
      const errors = {}
      this.form.title = this.form.title.trim()
      this.form.subtitle = this.form.subtitle.trim()
      this.form.slug = this.form.slug.trim()

      if (!this.form.title) {
        errors.title = this.$t('projects.course_title_required')
      }
      if (this.form.slug && !SLUG_PATTERN.test(this.form.slug)) {
        errors.slug = this.$t('projects.course_slug_invalid')
      }

      this.errors = errors
      return Object.keys(errors).length === 0
    },
    save() {
      if (!this.validate()) {
        return
      }

      this.saving = true
      const statusChanged = this.form.status !== this.savedForm.status

      CourseService.update(this.courseUuid, this.form, this.cover)
          .then((response) => statusChanged
              ? CourseService.updateStatus(this.courseUuid, this.form.status).then(() => response)
              : response)
          .then((response) => {
            const course = {...(response.data?.data ?? response.data), status: this.form.status}
            this.applyCourse({...this.form, ...course})
            NotificationService.showNotification(this.$t('projects.course_saved'), 'success')
            this.$emit('saved', course)
          })
          .catch((error) => {
            this.errors = this.fieldErrors(error)
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.saving = false
          })
    },
    // Errores de validación de Laravel (422) junto a cada campo.
    fieldErrors(error) {
      const errors = error?.response?.data?.errors
      if (!errors || Array.isArray(errors)) {
        return {}
      }
      return Object.fromEntries(Object.entries(errors).map(([field, messages]) => [field, [].concat(messages).join(' ')]))
    }
  }
}
</script>
